/**
 * Local-only mock for the NearestManagers endpoint, so the Managers page can be
 * exercised in the UI without a backend.
 *
 * Flip USE_MOCK_MANAGERS to true, run the app, open Configuration -> Managers.
 * Keep it false on master.
 */
export const USE_MOCK_MANAGERS = false;

// how long the fake request takes, so the loader and the infinite scroll are visible
const MOCK_LATENCY_MS = 400;

const MANAGERS = [
    {firstName: "Ingrid", lastName: "Haugen", email: "ingrid.haugen@example.com"},
    {firstName: "Petter", lastName: "Lindqvist", email: "petter.lindqvist@example.com"},
    {firstName: "Anneli", lastName: "Virtanen", email: "anneli.virtanen@example.com"},
    {firstName: "Bogdan", lastName: "Constantinescu-Vasilescu", email: "bogdan.constantinescu.vasilescu@a-very-long-company-domain.example.com"},
    {firstName: "Mette", lastName: "Sørensen", email: "mette.sorensen@example.com"}
];

// [firstName, lastName, email, index into MANAGERS]
const EMPLOYEES = [
    ["Anna", "Berg", "anna.berg@example.com", 0],
    ["Bjørn", "Dahl", "bjorn.dahl@example.com", 0],
    ["Camilla", "Eide", "camilla.eide@example.com", 0],
    ["David", "Fjeld", "david.fjeld@example.com", 0],
    ["Elin", "Gundersen", "elin.gundersen@example.com", 0],
    ["Fredrik", "Halvorsen", "fredrik.halvorsen@example.com", 0],
    ["Greta", "Isaksen", "greta.isaksen@example.com", 0],
    ["Håkon", "Jensen", "hakon.jensen@example.com", 0],
    ["Ida", "Karlsen", "ida.karlsen@example.com", 1],
    ["Jonas", "Larsson", "jonas.larsson@example.com", 1],
    ["Karin", "Magnusson", "karin.magnusson@example.com", 1],
    ["Lars", "Nyström", "lars.nystrom@example.com", 1],
    ["Maja", "Olsson", "maja.olsson@example.com", 1],
    ["Nils", "Persson", "nils.persson@example.com", 1],
    ["Olivia", "Qvist", "olivia.qvist@example.com", 1],
    ["Patrik", "Rehn", "patrik.rehn@example.com", 1],
    ["Quentin", "Salo", "quentin.salo@example.com", 2],
    ["Riikka", "Toivonen", "riikka.toivonen@example.com", 2],
    ["Sami", "Uusitalo", "sami.uusitalo@example.com", 2],
    ["Tuula", "Vainio", "tuula.vainio@example.com", 2],
    ["Ulla", "Wahlberg", "ulla.wahlberg@example.com", 2],
    ["Ville", "Ylitalo", "ville.ylitalo@example.com", 2],
    ["Wilma", "Aalto", "wilma.aalto@example.com", 2],
    ["Xenia", "Blomqvist", "xenia.blomqvist@example.com", 2],
    ["Alexandra-Maria", "Constantinescu-Popescu", "alexandra.maria.constantinescu.popescu@a-very-long-company-domain.example.com", 3],
    ["Cristian", "Dumitrescu", "cristian.dumitrescu@example.com", 3],
    ["Elena", "Ionescu", "elena.ionescu@example.com", 3],
    ["Florin", "Marinescu", "florin.marinescu@example.com", 3],
    ["Gabriela", "Nicolae", "gabriela.nicolae@example.com", 3],
    ["Horia", "Oprea", "horia.oprea@example.com", 3],
    ["Irina", "Petrescu", "irina.petrescu@example.com", 3],
    ["Jan", "Radu", "jan.radu@example.com", 3],
    ["Kasper", "Andersen", "kasper.andersen@example.com", 4],
    ["Line", "Bak", "line.bak@example.com", 4],
    ["Mads", "Christensen", "mads.christensen@example.com", 4],
    ["Nanna", "Dam", "nanna.dam@example.com", 4],
    ["Oskar", "Eriksen", "oskar.eriksen@example.com", 4],
    ["Pernille", "Frost", "pernille.frost@example.com", 4],
    ["Rasmus", "Gade", "rasmus.gade@example.com", 4],
    ["Sofie", "Holm", "sofie.holm@example.com", 4],
    ["Thea", "Iversen", "thea.iversen@example.com", 4],
    ["Ulrik", "Jakobsen", "ulrik.jakobsen@example.com", 4],
    ["Vera", "Kjær", "vera.kjaer@example.com", 4],
    ["William", "Lund", "william.lund@example.com", 4],
    ["Yara", "Mikkelsen", "yara.mikkelsen@example.com", 4],
    ["Zofia", "Nissen", "zofia.nissen@example.com", 4],
    ["Åse", "Overgaard", "aase.overgaard@example.com", 4]
];

const ALL_ROWS = EMPLOYEES.map(([firstName, lastName, email, managerIndex]) => {
    const manager = MANAGERS[managerIndex];
    return {
        employeeFirstName: firstName,
        employeeLastName: lastName,
        employeeEmail: email,
        managerFirstName: manager.firstName,
        managerLastName: manager.lastName,
        managerEmail: manager.email
    };
});

const employeeKey = (row) => `${row.employeeLastName} ${row.employeeFirstName} ${row.employeeEmail}`.toLowerCase();
const managerKey = (row) => `${row.managerLastName} ${row.managerFirstName} ${row.managerEmail}`.toLowerCase();

/**
 * Mirrors the shape returned by Api.getNearestManagers: {page, total, records, rows}.
 * `records` is the number of matching rows, `total` the number of pages.
 */
export function getMockNearestManagers(page, itemsPerPage, nearestManagerMode, asc, name) {
    const term = (name || "").trim().toLowerCase();

    const matching = term
        ? ALL_ROWS.filter(row => employeeKey(row).includes(term) || managerKey(row).includes(term))
        : ALL_ROWS.slice();

    // "employees by manager" groups on the manager, otherwise the employee leads
    const sortKey = nearestManagerMode === "EMPLOYEE_BY_MANAGER"
        ? (row) => managerKey(row) + employeeKey(row)
        : (row) => employeeKey(row) + managerKey(row);

    matching.sort((a, b) => {
        const compared = sortKey(a).localeCompare(sortKey(b));
        return asc ? compared : -compared;
    });

    const requestedPage = Math.max(1, page || 1);
    const from = (requestedPage - 1) * itemsPerPage;

    const response = {
        page: requestedPage,
        total: Math.ceil(matching.length / itemsPerPage),
        records: matching.length,
        rows: matching.slice(from, from + itemsPerPage)
    };

    return new Promise(resolve => setTimeout(() => resolve(response), MOCK_LATENCY_MS));
}
